/**
 * QuestPanel - Panneau des quêtes et défis ADHD-friendly
 *
 * Affiche les quêtes actives avec:
 * - Progression visuelle
 * - Récompenses XP
 * - Animations de complétion
 */

import React, { memo, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Flame, Gift, Heart, Check } from 'lucide-react';

// Icônes par type de quête
const QUEST_ICONS = {
  daily: { icon: Star, color: '#fbbf24' },
  streak: { icon: Flame, color: '#f97316' },
  boss: { icon: Trophy, color: '#a855f7' },
  secret: { icon: Gift, color: '#ec4899' },
  recovery: { icon: Heart, color: '#f472b6' },
};

const QuestPanel = memo(function QuestPanel({
  quests = [],
  onQuestClick,
  compact = false,
}) {
  // Séparer quêtes complétées et actives
  const { activeQuests, completedQuests } = useMemo(() => {
    const active = quests.filter(q => !q.completed);
    const completed = quests.filter(q => q.completed);
    return { activeQuests: active, completedQuests: completed };
  }, [quests]);

  // XP total disponible
  const totalXP = useMemo(() => {
    return activeQuests.reduce((sum, q) => sum + (q.xp || 0), 0);
  }, [activeQuests]);

  if (quests.length === 0) {
    return (
      <div className="p-4 bg-zinc-800/30 rounded-xl border border-zinc-700/50 text-center">
        <Star className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
        <p className="text-sm text-zinc-500">Pas de quêtes pour aujourd'hui</p>
        <p className="text-xs text-zinc-600 mt-1">Génère ta semaine pour débloquer des défis !</p>
      </div>
    );
  }

  // Mode compact pour mobile
  if (compact) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 rounded-xl overflow-x-auto">
        {activeQuests.slice(0, 3).map((quest) => {
          const questConfig = QUEST_ICONS[quest.type] || QUEST_ICONS.daily;
          const Icon = questConfig.icon;
          const progress = quest.objectives?.filter(o => o.done).length / quest.objectives?.length || 0;

          return (
            <motion.div
              key={quest.id}
              className="flex items-center gap-1.5 px-2 py-1 bg-zinc-700/50 rounded-lg flex-shrink-0"
              whileTap={{ scale: 0.95 }}
              onClick={() => onQuestClick?.(quest)}
            >
              <Icon className="w-3.5 h-3.5" style={{ color: questConfig.color }} />
              <span className="text-xs text-zinc-300 whitespace-nowrap">{quest.title}</span>
              <div className="w-8 h-1.5 bg-zinc-600 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${progress * 100}%`, backgroundColor: questConfig.color }}
                />
              </div>
            </motion.div>
          );
        })}
        {totalXP > 0 && (
          <span className="text-xs text-yellow-400 font-medium whitespace-nowrap">
            +{totalXP} XP
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header avec XP total */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-yellow-400" />
          Quêtes du jour
        </h3>
        {totalXP > 0 && (
          <span className="text-xs font-bold text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded-full">
            +{totalXP} XP disponibles
          </span>
        )}
      </div>

      {/* Liste des quêtes actives */}
      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {activeQuests.map((quest) => (
            <QuestCard
              key={quest.id}
              quest={quest}
              onClick={() => onQuestClick?.(quest)}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Quêtes complétées */}
      {completedQuests.length > 0 && (
        <div className="pt-2 border-t border-zinc-800">
          <p className="text-xs text-zinc-500 mb-2">
            Complétées ({completedQuests.length})
          </p>
          <div className="space-y-1">
            {completedQuests.map((quest) => (
              <motion.div
                key={quest.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 rounded-lg"
              >
                <Check className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-300 line-through opacity-70">
                  {quest.title}
                </span>
                <span className="text-xs text-emerald-400 ml-auto">+{quest.xp} XP</span>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * QuestCard - Carte individuelle de quête
 */
const QuestCard = memo(function QuestCard({ quest, onClick }) {
  const questConfig = QUEST_ICONS[quest.type] || QUEST_ICONS.daily;
  const Icon = questConfig.icon;

  // Calculer la progression
  const completedObjectives = quest.objectives?.filter(o => o.done).length || 0;
  const totalObjectives = quest.objectives?.length || 1;
  const progress = completedObjectives / totalObjectives;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="relative overflow-hidden rounded-xl border transition-colors cursor-pointer group"
      style={{
        backgroundColor: `${questConfig.color}08`,
        borderColor: `${questConfig.color}30`,
      }}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={onClick}
    >
      {/* Barre de progression en arrière-plan */}
      <div
        className="absolute inset-0 transition-all duration-500"
        style={{
          width: `${progress * 100}%`,
          backgroundColor: `${questConfig.color}15`,
        }}
      />

      <div className="relative p-3">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div
            className="p-2 rounded-lg"
            style={{ backgroundColor: `${questConfig.color}20` }}
          >
            <Icon className="w-5 h-5" style={{ color: questConfig.color }} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-zinc-200 truncate">
                {quest.title}
              </h4>
              {quest.isSecret && (
                <span className="text-xs px-1.5 py-0.5 bg-pink-500/20 text-pink-300 rounded">
                  Secret
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-400 mt-0.5">{quest.description}</p>
          </div>

          <div className="text-right flex-shrink-0">
            <span className="text-sm font-bold" style={{ color: questConfig.color }}>
              +{quest.xp}
            </span>
            <span className="text-xs text-zinc-500 block">XP</span>
          </div>
        </div>

        {/* Objectifs */}
        <div className="mt-3 space-y-1.5">
          {quest.objectives?.map((obj, idx) => (
            <div
              key={idx}
              className={`flex items-center gap-2 text-xs ${
                obj.done ? 'text-zinc-500 line-through' : 'text-zinc-300'
              }`}
            >
              <div
                className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                  obj.done
                    ? 'border-emerald-500 bg-emerald-500/20'
                    : 'border-zinc-600'
                }`}
              >
                {obj.done && <Check className="w-2.5 h-2.5 text-emerald-400" />}
              </div>
              <span>{obj.label}</span>
            </div>
          ))}
        </div>

        {/* Barre de progression */}
        <div className="mt-3 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: questConfig.color }}
            initial={{ width: 0 }}
            animate={{ width: `${progress * 100}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
        <p className="text-[10px] text-zinc-500 mt-1 text-right">
          {completedObjectives}/{totalObjectives} objectifs
        </p>
      </div>
    </motion.div>
  );
});

export { QuestPanel };
